import React, { useEffect, useState } from 'react';
import { fetchProofs, fetchClaims } from '../api/ledger';

type TabType = 'proofs' | 'claims';

export default function Records() {
  const [tab, setTab] = useState<TabType>('proofs');
  const [proofs, setProofs] = useState<any[]>([]);
  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProofs().catch(() => []),
      fetchClaims().catch(() => []),
    ]).then(([p, c]) => {
      setProofs(p);
      setClaims(c);
      setLoading(false);
    });
  }, []);

  const filteredProofs = proofs.filter((p) =>
    !search || JSON.stringify(p).toLowerCase().includes(search.toLowerCase())
  );

  const filteredClaims = claims.filter((c) =>
    !search || JSON.stringify(c).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ledger-container">
      <div className="ledger-header">
        <h1>📋 Ledger Records</h1>
        <p>모든 Proof 및 Claim 기록을 조회합니다.</p>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          className={`ledger-button ${tab === 'proofs' ? '' : 'secondary'}`}
          onClick={() => setTab('proofs')}
        >
          ✅ Proofs ({proofs.length})
        </button>
        <button
          className={`ledger-button ${tab === 'claims' ? '' : 'secondary'}`}
          onClick={() => setTab('claims')}
        >
          📝 Claims ({claims.length})
        </button>
        <div style={{ flex: 1 }}>
          <input
            className="ledger-input"
            placeholder="🔍 Search records..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="ledger-card">
          <div className="ledger-loading">Loading records...</div>
        </div>
      ) : tab === 'proofs' ? (
        <div className="ledger-table-container">
          {filteredProofs.length === 0 ? (
            <div className="ledger-empty">
              No proofs found. <a href="/proofs" style={{ color: 'var(--ledger-accent)' }}>Submit a proof</a>
            </div>
          ) : (
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Type</th>
                  <th>Statement</th>
                  <th>Artifacts</th>
                  <th>Score</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredProofs.map((proof, i) => (
                  <tr key={i}>
                    <td><span className="ledger-hash">{proof.agentId || proof.agent_id || '—'}</span></td>
                    <td>{proof.claimType || proof.claim?.type || '—'}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {proof.statement || proof.claim?.statement || '—'}
                    </td>
                    <td>{Array.isArray(proof.artifacts) ? proof.artifacts.join(', ') : proof.evidence?.artifacts?.join(', ') || '—'}</td>
                    <td style={{ color: (proof.verification?.score || 0) >= 0.8 ? 'var(--ledger-success)' : 'var(--ledger-warning)' }}>
                      {proof.verification?.score?.toFixed(2) || '—'}
                    </td>
                    <td>
                      <span className={`ledger-badge ${(proof.verification?.score || 0) >= 0.8 ? 'verified' : 'pending'}`}>
                        {(proof.verification?.score || 0) >= 0.8 ? '✓ Verified' : '⏳ Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="ledger-table-container">
          {filteredClaims.length === 0 ? (
            <div className="ledger-empty">
              No claims found. <a href="/proofs" style={{ color: 'var(--ledger-accent)' }}>Register a claim</a>
            </div>
          ) : (
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Type</th>
                  <th>Statement</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((claim, i) => (
                  <tr key={i}>
                    <td><span className="ledger-hash">{claim.agentId || '—'}</span></td>
                    <td>{claim.type || '—'}</td>
                    <td>{claim.statement || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="ledger-card" style={{ marginTop: '24px' }}>
        <div className="ledger-card-header">
          <span className="ledger-card-title"><span className="ledger-icon">🔒</span> Data Integrity</span>
        </div>
        <div className="ledger-code-block">
{`{
  "verification": {
    "method": "consensus",
    "threshold": "2/3 verifiers",
    "immutability": "blockchain-anchored",
    "transparency": "fully auditable"
  },
  "storage": "Cloudflare D1 + Sui Move",
  "encryption": "AES-256 + Digital Signatures"
}`}
        </div>
      </div>
    </div>
  );
}
